/* =============================================================================
   XP Category Jump Nav: assets/xp-category-nav.js
   Loaded by sections/category-jump-nav.liquid.

   States (data-state on .xp-cjn, also sent to GA4 as nav_state):
     inline         in page flow
     stuck-desktop  fixed below the header (>=992px)
     fixed-mobile   fixed to the viewport bottom (<=991px)

   Page position uses no scroll listeners. Two IntersectionObservers watch a
   region running from far above the viewport down to a line, so they fire when
   something crosses that line, even on a jump that skips past it (End key,
   fling). Each callback then reads positions once and works out the state.
   Stick trigger: .xp-cjn itself. Don't use an empty element for this, because
   global.css sets div:empty { display: none }, which gives it a zero-size rect.
   ============================================================================= */

(function () {
  'use strict';

  if (window.xpCategoryNavLoaded) return;
  window.xpCategoryNavLoaded = true;

  var STATE_INLINE = 'inline';
  var STATE_STUCK = 'stuck-desktop';
  var STATE_FIXED = 'fixed-mobile';

  var LANDING_GAP = 16; /* space between the header + bar and the section top */
  var ACTIVE_SLOP = 8; /* a pill activates this far below the landing point */
  var REGION_ABOVE = 1000000; /* px: effectively everything above the line */
  var SCROLL_LOCK_MS = 1200; /* fallback where scrollend isn't supported */

  var mobileQuery = window.matchMedia('(max-width: 991px)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var instances = [];
  var reinitFrame = null;

  /* ---------------------------------------------------------------------------
     ONE-TIME SCROLL NUDGE. Self-contained: to remove it, delete this function
     and the setupNudge call in init(). Returns its own cleanup function.
     --------------------------------------------------------------------------- */
  function setupNudge(root, list) {
    var STORAGE_KEY = 'xpCjnNudgeSeen';
    var DISTANCE = 50;
    var STEP_MS = 500;
    var START_DELAY_MS = 250;
    var INPUT_EVENTS = ['touchstart', 'wheel', 'keydown'];
    var designMode = window.Shopify && window.Shopify.designMode;
    var timers = [];
    var observer = null;

    function noop() {}

    function alreadySeen() {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (error) {
        return false;
      }
    }

    function markSeen() {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } catch (error) {
        /* Storage blocked: it may replay on the next page load, which is harmless */
      }
    }

    function stop() {
      timers.forEach(clearTimeout);
      timers = [];
      INPUT_EVENTS.forEach(function (name) {
        window.removeEventListener(name, onInput);
      });
    }

    function onInput(event) {
      stop();
      /* Leave the row alone if they're touching it; otherwise undo the offset */
      if (!(event.target instanceof Node) || !list.contains(event.target)) list.scrollLeft = 0;
    }

    function play() {
      markSeen();
      INPUT_EVENTS.forEach(function (name) {
        window.addEventListener(name, onInput, { passive: true });
      });
      timers.push(setTimeout(function () {
        list.scrollTo({ left: DISTANCE, behavior: 'smooth' });
      }, START_DELAY_MS));
      timers.push(setTimeout(function () {
        list.scrollTo({ left: 0, behavior: 'smooth' });
      }, START_DELAY_MS + STEP_MS));
      timers.push(setTimeout(stop, START_DELAY_MS + STEP_MS * 2));
    }

    if (designMode || reducedMotion.matches || !mobileQuery.matches || alreadySeen()) return noop;

    observer = new IntersectionObserver(function (entries) {
      var visible = entries.some(function (entry) { return entry.isIntersecting; });
      if (!visible || root.getAttribute('data-state') !== 'inline') return;
      observer.disconnect();
      if (list.scrollWidth > list.clientWidth && list.scrollLeft === 0) play();
    }, { threshold: 0.6 });
    observer.observe(root);

    return function cleanup() {
      observer.disconnect();
      stop();
    };
  }

  function init(root) {
    var alreadyRunning = instances.some(function (instance) { return instance.root === root; });
    if (alreadyRunning) return;

    var bar = root.querySelector('[data-xp-cjn-bar]');
    var list = root.querySelector('[data-xp-cjn-list]');
    if (!bar || !list) return;

    /* Pills whose target isn't on the page keep their native anchor behavior */
    var items = [];
    root.querySelectorAll('[data-xp-cjn-pill]').forEach(function (pill) {
      var target = document.getElementById(pill.getAttribute('data-target'));
      if (target) items.push({ pill: pill, target: target });
    });

    var state = STATE_INLINE;
    var hidden = false;
    var activeItem = null;
    var inlineHeight = 0;
    var barHeight = 0;
    var stickLine = 0; /* viewport y where the bar leaves page flow */
    var landingOffset = 0; /* viewport y where a clicked section's top lands */
    var activeLine = 0; /* viewport y that decides the active section */
    var lastWidth = null;
    var lastSignature = null;
    var observers = [];
    var frame = null;
    var resizeTimer = null;
    var endScrollLock = null;
    var destroyed = false;
    var cleanupNudge = null;

    function headerHeight() {
      if (mobileQuery.matches) {
        /* Full height even while the nav is slid away: it comes back on any
           scroll up, and a clicked heading must never end up under it */
        var mobileNav = document.querySelector('.xp-mobile-nav');
        return mobileNav ? mobileNav.offsetHeight : 0;
      }
      var header = document.querySelector('.section-header');
      return header ? Math.floor(header.getBoundingClientRect().height) : 0;
    }

    /* Height of the bar in a given state, without visibly switching to it */
    function measureBar(stateName) {
      var current = root.getAttribute('data-state');
      root.classList.add('xp-cjn--no-anim');
      root.setAttribute('data-state', stateName);
      var height = bar.getBoundingClientRect().height;
      root.setAttribute('data-state', current);
      void bar.offsetHeight; /* apply the restore before transitions return */
      root.classList.remove('xp-cjn--no-anim');
      return height;
    }

    function observeLine(line, elements) {
      var options = {
        rootMargin: REGION_ABOVE + 'px 0px ' + Math.min(0, line - window.innerHeight) + 'px 0px',
        threshold: [0, 1],
        root: document /* keeps rootMargin working inside the theme editor iframe */
      };
      var observer;
      try {
        observer = new IntersectionObserver(scheduleEvaluate, options);
      } catch (error) {
        delete options.root;
        observer = new IntersectionObserver(scheduleEvaluate, options);
      }
      elements.forEach(function (element) {
        observer.observe(element);
      });
      observers.push(observer);
    }

    function disconnectObservers() {
      observers.forEach(function (observer) {
        observer.disconnect();
      });
      observers = [];
    }

    function refresh(forceMeasure) {
      var mobile = mobileQuery.matches;
      var width = window.innerWidth;

      if (destroyed) return;

      if (forceMeasure === true || width !== lastWidth) {
        lastWidth = width;
        inlineHeight = measureBar(STATE_INLINE);
        barHeight = measureBar(mobile ? STATE_FIXED : STATE_STUCK);
        document.documentElement.style.setProperty('--xp-cjn-bar-height', barHeight + 'px');
        if (state !== STATE_INLINE) root.style.height = inlineHeight + 'px';
      }

      var header = headerHeight();
      stickLine = mobile ? 0 : header;
      landingOffset = (mobile ? header : header + barHeight) + LANDING_GAP;
      activeLine = landingOffset + ACTIVE_SLOP;

      var signature = [stickLine, activeLine, window.innerHeight].join('|');
      if (signature !== lastSignature) {
        lastSignature = signature;
        disconnectObservers();
        observeLine(stickLine, [root]);
        if (items.length) {
          observeLine(activeLine, items.map(function (item) { return item.target; }));
        }
      }

      updateFades();
      scheduleEvaluate();
    }

    function updateFades() {
      var maxScroll = list.scrollWidth - list.clientWidth;
      root.classList.toggle('xp-cjn--fade-left', list.scrollLeft > 1);
      root.classList.toggle('xp-cjn--fade-right', list.scrollLeft < maxScroll - 1);
    }

    function scheduleEvaluate() {
      if (frame !== null) return;
      frame = requestAnimationFrame(function () {
        frame = null;
        evaluate();
      });
    }

    function evaluate() {
      /* Desktop: its top reaches the header. Mobile: the whole inline row has
         scrolled off the top of the viewport. */
      var mobile = mobileQuery.matches;
      var rootRect = root.getBoundingClientRect();
      var engaged = mobile ? rootRect.bottom <= stickLine : rootRect.top < stickLine;
      var nextState = STATE_INLINE;
      if (engaged) nextState = mobile ? STATE_FIXED : STATE_STUCK;

      var firstTop = Infinity;
      var lastBottom = -Infinity;
      var current = null;

      items.forEach(function (item) {
        var rect = item.target.getBoundingClientRect();
        firstTop = Math.min(firstTop, rect.top);
        lastBottom = Math.max(lastBottom, rect.bottom);
        if (rect.top <= activeLine && rect.bottom > activeLine) current = item;
      });

      var pastEnd = items.length > 0 && lastBottom <= activeLine;
      var aboveStart = firstTop > activeLine;

      applyState(nextState, pastEnd);

      /* Between sections (a margin at the line), keep the last active pill.
         During a click's smooth scroll, the clicked pill stays active. */
      if (!endScrollLock && (current || pastEnd || aboveStart)) setActive(current);
    }

    function applyState(nextState, pastEnd) {
      var nextHidden = pastEnd && nextState !== STATE_INLINE;
      if (nextState === state && nextHidden === hidden) return;

      var enteringBottom = nextState === STATE_FIXED && state !== STATE_FIXED;

      root.style.height = nextState === STATE_INLINE ? '' : inlineHeight + 'px';
      root.setAttribute('data-state', nextState);
      state = nextState;

      if (enteringBottom && !nextHidden) {
        /* Start below the viewport so removing --hidden slides it up */
        root.classList.add('xp-cjn--no-anim', 'xp-cjn--hidden');
        void bar.offsetHeight;
        root.classList.remove('xp-cjn--no-anim');
        void bar.offsetHeight;
      }

      root.classList.toggle('xp-cjn--hidden', nextHidden);
      hidden = nextHidden;

      document.body.classList.toggle('xp-cjn-bottom-visible', state === STATE_FIXED && !hidden);
    }

    function setActive(item) {
      if (item === activeItem) return;
      if (activeItem) activeItem.pill.removeAttribute('aria-current');
      activeItem = item;
      if (!item) return;
      item.pill.setAttribute('aria-current', 'true');
      revealPill(item.pill);
    }

    /* Scrolls the pill row only. scrollIntoView would also move the page. */
    function revealPill(pill) {
      if (list.scrollWidth <= list.clientWidth) return;
      list.scrollTo({
        left: pill.offsetLeft - (list.clientWidth - pill.offsetWidth) / 2,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    function landingTop(target) {
      return window.scrollY + target.getBoundingClientRect().top - landingOffset;
    }

    function scrollToItem(item) {
      if (endScrollLock) endScrollLock(false);

      var timer = null;

      function onInterrupt() {
        if (endScrollLock) endScrollLock(false);
      }

      function onScrollEnd() {
        if (endScrollLock) endScrollLock(true);
      }

      endScrollLock = function (finished) {
        endScrollLock = null;
        clearTimeout(timer);
        window.removeEventListener('scrollend', onScrollEnd);
        window.removeEventListener('wheel', onInterrupt);
        window.removeEventListener('touchstart', onInterrupt);

        /* Lazy images can shift the page during the scroll: settle on the target */
        if (finished && Math.abs(item.target.getBoundingClientRect().top - landingOffset) > 4) {
          window.scrollTo({ top: landingTop(item.target), behavior: 'auto' });
        }
        scheduleEvaluate();
      };

      /* Only releases the lock: it may fire mid-scroll, so it must not correct */
      timer = setTimeout(onInterrupt, SCROLL_LOCK_MS);
      window.addEventListener('scrollend', onScrollEnd);
      window.addEventListener('wheel', onInterrupt, { passive: true });
      window.addEventListener('touchstart', onInterrupt, { passive: true });

      window.scrollTo({
        top: landingTop(item.target),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    function track(pill) {
      /* GA4 runs in a sandboxed pixel; the "XP Category Nav" custom pixel in
         Shopify admin subscribes to this event and forwards it */
      if (!window.Shopify || !window.Shopify.analytics || !window.Shopify.analytics.publish) return;
      window.Shopify.analytics.publish('category_nav_click', {
        category: pill.getAttribute('data-category'),
        position: parseInt(pill.getAttribute('data-position'), 10),
        nav_state: state
      });
    }

    function onClick(event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var pill = event.target.closest('[data-xp-cjn-pill]');
      if (!pill) return;

      var item = items.filter(function (candidate) {
        return candidate.pill === pill;
      })[0];
      if (!item) return;

      event.preventDefault(); /* also keeps the URL hash unchanged */
      track(pill);
      setActive(item);
      scrollToItem(item);
    }

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refresh, 100);
    }

    function onLoad() {
      refresh(true); /* fonts and images can change measured heights */
    }

    function onHeaderChange() {
      refresh();
    }

    function onBreakpointChange() {
      refresh(true);
    }

    bar.addEventListener('click', onClick);
    list.addEventListener('scroll', updateFades, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onLoad);
    document.addEventListener('xp:header-offset', onHeaderChange);
    mobileQuery.addEventListener('change', onBreakpointChange);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(onLoad);

    refresh(true);
    cleanupNudge = setupNudge(root, list);

    instances.push({
      root: root,
      /* Leaves no observers or listeners, and the element back as rendered */
      destroy: function () {
        destroyed = true;
        if (endScrollLock) endScrollLock(false);
        cleanupNudge();
        disconnectObservers();
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
        clearTimeout(resizeTimer);
        bar.removeEventListener('click', onClick);
        list.removeEventListener('scroll', updateFades);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('load', onLoad);
        document.removeEventListener('xp:header-offset', onHeaderChange);
        mobileQuery.removeEventListener('change', onBreakpointChange);
        document.body.classList.remove('xp-cjn-bottom-visible');
        document.documentElement.style.removeProperty('--xp-cjn-bar-height');
        setActive(null);
        root.setAttribute('data-state', STATE_INLINE);
        root.classList.remove('xp-cjn--hidden', 'xp-cjn--fade-left', 'xp-cjn--fade-right', 'xp-cjn--no-anim');
        root.style.height = '';
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-xp-cjn]').forEach(init);
  }

  /* Theme editor. A pill's target can be any section on the page, so a change to
     any section can replace a target or move the nav. Rebuild everything from the
     current DOM. Teardown is immediate; init waits a frame, once the editor has
     finished changing the page, and several events in a row share one init. */
  function reinitAll() {
    instances.forEach(function (instance) {
      instance.destroy();
    });
    instances = [];
    if (reinitFrame !== null) return;
    reinitFrame = requestAnimationFrame(function () {
      reinitFrame = null;
      initAll();
    });
  }

  initAll();

  document.addEventListener('shopify:section:load', reinitAll);
  document.addEventListener('shopify:section:unload', reinitAll);
  document.addEventListener('shopify:section:reorder', reinitAll);
})();
